import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  /** Which social sign-in providers are configured — { google, microsoft } booleans. */
  oauthProviders() {
    return this.request('/auth/oauth-providers');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Public course catalogue ----
  listPathways() {
    return this.request('/catalogue/pathways');
  }

  /** The XC Trilogy relationship/dependency model. */
  getCurriculum() {
    return this.request('/catalogue/curriculum');
  }

  /** Public openings teaser — roles graduates get deployed into (no auth). */
  listOpenings() {
    return this.request('/jobs/openings');
  }

  listCourses({ search } = {}) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/catalogue/courses${qs}`);
  }

  // ---- Stickiness / nudges ----
  getNudgeDigest() {
    return this.request('/nudge');
  }

  getNudgePreferences() {
    return this.request('/nudge/preferences');
  }

  updateNudgePreferences(prefs) {
    return this.request('/nudge/preferences', { method: 'PUT', body: prefs });
  }

  snoozeNudges(days = 7) {
    return this.request('/nudge/snooze', { method: 'POST', body: { days } });
  }

  getSyllabus(level) {
    return this.request(`/catalogue/syllabus/${level}`);
  }

  // ---- Learner profile ----
  getLearnerProfile() {
    return this.request('/learner/profile');
  }

  saveLearnerProfile(profile) {
    return this.request('/learner/profile', { method: 'PUT', body: profile });
  }

  getLearnerDashboard() {
    return this.request('/learner/dashboard');
  }

  bookOfficeHours({ date, time }) {
    return this.request('/learner/office-hours', { method: 'POST', body: { date, time } });
  }

  // ---- Public intake assessment ----
  matchAssessment(input) {
    return this.request('/assessment/match', { method: 'POST', body: input });
  }

  /** The signed-in learner's most recent assessment outcome (or null). */
  latestAssessment() {
    return this.request('/assessment/latest');
  }

  // ---- Cohort enrollment + learning contract ----
  checkEnrollmentEligibility(course) {
    return this.request(`/enrollment/eligibility?course=${encodeURIComponent(course)}`);
  }

  getMentorSlots() {
    return this.request('/enrollment/mentor-slots');
  }

  /** The signed-in learner's own enrollments. */
  myEnrollments() {
    return this.request('/enrollment/mine');
  }

  enroll(payload) {
    return this.request('/enrollment', { method: 'POST', body: payload });
  }

  // ---- Checkout & payment ----
  getOrder(course, coupon) {
    const qs = coupon ? `?course=${encodeURIComponent(course)}&coupon=${encodeURIComponent(coupon)}` : `?course=${encodeURIComponent(course)}`;
    return this.request(`/payment/order${qs}`);
  }

  applyCoupon(course, code) {
    return this.request('/payment/coupon', { method: 'POST', body: { course, code } });
  }

  checkout(payload) {
    return this.request('/payment/checkout', { method: 'POST', body: payload });
  }

  getPaymentStatus(course) {
    return this.request(`/payment/status?course=${encodeURIComponent(course)}`);
  }

  // ---- Auth: password reset ----
  forgotPassword(email) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword(token, password) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  // ---- Stripe checkout ----
  createStripeSession(payload) {
    return this.request('/payment/stripe/checkout-session', { method: 'POST', body: payload });
  }

  confirmStripeSession(sessionId) {
    return this.request(`/payment/stripe/confirm?session_id=${encodeURIComponent(sessionId)}`);
  }

  // ---- Platform Settings (admin) ----
  getPlatformSettings() {
    return this.request('/settings');
  }

  updatePlatformSettings(section, payload) {
    return this.request(`/settings/${encodeURIComponent(section)}`, { method: 'PUT', body: payload });
  }

  testLlmSettings() {
    return this.request('/settings/llm/test', { method: 'POST', body: {} });
  }

  testSmtpSettings(to) {
    return this.request('/settings/smtp/test', { method: 'POST', body: { to } });
  }

  // ---- Course Player ----
  /** The player's course selector list — [{ key, title }]. */
  listPlayerCourses() {
    return this.request('/course/courses');
  }

  getCourseStructure(course) {
    return this.request(`/course/structure${course ? `?course=${encodeURIComponent(course)}` : ''}`);
  }

  getCourseSession(sessionId) {
    return this.request(`/course/sessions/${encodeURIComponent(sessionId)}`);
  }

  completeCourseSession(sessionId) {
    return this.request(`/course/sessions/${encodeURIComponent(sessionId)}/complete`, {
      method: 'POST',
    });
  }

  completeExternalCourseSession(sessionId, { evidenceType, evidenceRef }) {
    return this.request(`/course/sessions/${encodeURIComponent(sessionId)}/external-complete`, {
      method: 'POST',
      body: { evidenceType, evidenceRef },
    });
  }

  listSessionPosts(sessionId, thread = 'cohort') {
    return this.request(
      `/course/sessions/${encodeURIComponent(sessionId)}/posts?thread=${encodeURIComponent(thread)}`
    );
  }

  createSessionPost(sessionId, body, thread = 'cohort') {
    return this.request(`/course/sessions/${encodeURIComponent(sessionId)}/posts`, {
      method: 'POST',
      body: { body, thread },
    });
  }

  deleteSessionPost(postId) {
    return this.request(`/course/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });
  }

  submitLessonFeedback(payload) {
    return this.request('/course/feedback', { method: 'POST', body: payload });
  }

  submitCourseFeedback(payload) {
    return this.request('/course/course-feedback', { method: 'POST', body: payload });
  }

  getModuleChallenge(moduleId) {
    return this.request(`/course/modules/${encodeURIComponent(moduleId)}/challenge`);
  }

  // ---- Course Player rich chat / engagement ----
  askAiTutor(sessionId, query) {
    return this.request('/engagement/ai-tutor', { method: 'POST', body: { sessionId, query } });
  }

  getAiTutorHistory() {
    return this.request('/engagement/ai-tutor/history');
  }

  getTelegramStatus() {
    return this.request('/engagement/telegram');
  }

  linkTelegram() {
    return this.request('/engagement/telegram/link', { method: 'POST' });
  }

  confirmTelegram(token) {
    return this.request('/engagement/telegram/confirm', { method: 'POST', body: { token } });
  }

  listNotifications() {
    return this.request('/engagement/notifications');
  }

  tagNotify(kind = 'tag') {
    return this.request('/engagement/notify', { method: 'POST', body: { kind } });
  }

  submitModuleChallenge(moduleId, answers) {
    return this.request(`/course/modules/${encodeURIComponent(moduleId)}/challenge/submit`, {
      method: 'POST',
      body: { answers },
    });
  }

  // ---- Assessment Center (evaluation) ----
  getQuiz() {
    return this.request('/evaluation/quiz');
  }

  submitQuiz(answers) {
    return this.request('/evaluation/quiz/submit', { method: 'POST', body: { answers } });
  }

  getAssignment() {
    return this.request('/evaluation/assignment');
  }

  submitAssignment(payload) {
    return this.request('/evaluation/assignment', { method: 'POST', body: payload });
  }

  listPeerReviews() {
    return this.request('/evaluation/peer-reviews');
  }

  gradePeer(candidateCode, payload) {
    return this.request(`/evaluation/peer-reviews/${encodeURIComponent(candidateCode)}/grade`, {
      method: 'POST',
      body: payload,
    });
  }

  // ---- Mentor/Supervisor grading (admin) ----
  getGradingQueue(type) {
    const qs = type ? `?type=${encodeURIComponent(type)}` : '';
    return this.request(`/grading/queue${qs}`);
  }

  getGradingAssignment(key) {
    return this.request(`/grading/assignments/${encodeURIComponent(key)}`);
  }

  confirmGrade(key, payload) {
    return this.request(`/grading/assignments/${encodeURIComponent(key)}/grade`, {
      method: 'POST',
      body: payload,
    });
  }

  getExternalEvidence(key) {
    return this.request(`/grading/external/${encodeURIComponent(key)}`);
  }

  approveExternalEvidence(key) {
    return this.request(`/grading/external/${encodeURIComponent(key)}/approve`, { method: 'POST' });
  }

  // ---- Learner credential / certificate ----
  getCredential() {
    return this.request('/credential');
  }

  /** Public registry verification — resolve a credential id (no auth required). */
  verifyCredential(credentialId) {
    return this.request(`/credential/verify/${encodeURIComponent(credentialId)}`);
  }

  // ---- Pay-to-certify ----
  getCertificationStatus() {
    return this.request('/credential/certification');
  }

  createCertStripeSession(couponCode) {
    return this.request('/credential/certification/stripe-session', { method: 'POST', body: { couponCode } });
  }

  redeemCertification({ couponCode, method }) {
    return this.request('/credential/certification/redeem', {
      method: 'POST',
      body: { couponCode, method },
    });
  }

  // ---- Identity verification (simulated KYC) — gates credential issuance ----
  getIdentityStatus() {
    return this.request('/identity');
  }

  submitIdentity({ legalName, idType, idReference }) {
    return this.request('/identity', { method: 'POST', body: { legalName, idType, idReference } });
  }

  // ---- Sandbox capstone ----
  getCapstoneState() {
    return this.request('/capstone');
  }

  provisionSandbox() {
    return this.request('/capstone/provision', { method: 'POST' });
  }

  runCapstoneDiagnostics() {
    return this.request('/capstone/diagnostics', { method: 'POST' });
  }

  getDefenseSlots() {
    return this.request('/capstone/defense-slots');
  }

  submitCapstone(payload) {
    return this.request('/capstone/submit', { method: 'POST', body: payload });
  }

  bookDefense(slotId) {
    return this.request('/capstone/defense/book', { method: 'POST', body: { slotId } });
  }

  /** Fetches the certificate PDF as a Blob (with auth) for a client-side download. */
  async downloadCredentialPdf() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/credential/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const payload = await res.json();
        message = payload?.error?.message || message;
      } catch {
        // non-JSON error body — keep the default message
      }
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return { blob, fileName: match ? match[1] : 'certificate.pdf' };
  }

  // ---- Graduate Job Board -------------------------------------------------
  getJobBoard() {
    return this.request('/jobs/board');
  }

  getJobDetail(jobId) {
    return this.request(`/jobs/${encodeURIComponent(jobId)}`);
  }

  applyToJob(jobId, payload) {
    return this.request(`/jobs/${encodeURIComponent(jobId)}/apply`, { method: 'POST', body: payload });
  }

  getJobApplications() {
    return this.request('/jobs/applications');
  }

  setJobAlerts(applicationId, enabled) {
    return this.request(`/jobs/applications/${encodeURIComponent(applicationId)}/alerts`, {
      method: 'PATCH',
      body: { enabled },
    });
  }

  advanceJobStage(applicationId, stage) {
    return this.request(`/jobs/applications/${encodeURIComponent(applicationId)}/stage`, {
      method: 'POST',
      body: { stage },
    });
  }

  // ---- Course Studio / Course Workspace ----------------------------------
  getCourseWorkspace() {
    return this.request('/studio/workspace');
  }

  getCourseIntentOptions() {
    return this.request('/studio/intent-options');
  }

  draftCourse(intent) {
    return this.request('/studio/draft-course', { method: 'POST', body: intent });
  }

  // ---- Module Composer ----
  getComposerOptions() {
    return this.request('/studio/composer/options');
  }

  getComposition() {
    return this.request('/studio/composer/composition');
  }

  resetComposition() {
    return this.request('/studio/composer/reset', { method: 'POST' });
  }

  searchRegistry(body) {
    return this.request('/studio/composer/search', { method: 'POST', body });
  }

  linkModule(moduleId) {
    return this.request('/studio/composer/link', { method: 'POST', body: { moduleId } });
  }

  forkModule(moduleId) {
    return this.request('/studio/composer/fork', { method: 'POST', body: { moduleId } });
  }

  createElectiveSlot(body) {
    return this.request('/studio/composer/elective-slot', { method: 'POST', body });
  }

  // ---- External Modules Manager ----
  getExternalOptions() {
    return this.request('/studio/external/options');
  }

  getExternalModules() {
    return this.request('/studio/external/modules');
  }

  resetExternalModules() {
    return this.request('/studio/external/reset', { method: 'POST' });
  }

  draftExternalMapping(body) {
    return this.request('/studio/external/draft', { method: 'POST', body });
  }

  confirmExternalModule(body) {
    return this.request('/studio/external/confirm', { method: 'POST', body });
  }

  probeExternalModule(moduleId) {
    return this.request(`/studio/external/${encodeURIComponent(moduleId)}/probe`, { method: 'POST' });
  }

  // ---- AI Tuner: session orchestration + video render ----
  getSessionOptions() {
    return this.request('/studio/session/options');
  }

  orchestrateSession(body) {
    return this.request('/studio/session/orchestrate', { method: 'POST', body });
  }

  getSessionContent(sessionId) {
    return this.request(`/studio/session/${encodeURIComponent(sessionId)}/content`);
  }

  renderSessionVideo(body) {
    return this.request('/studio/session/render', { method: 'POST', body });
  }

  getRenderStatus(sessionId) {
    return this.request(`/studio/session/${encodeURIComponent(sessionId)}/render-status`);
  }

  getAssessmentBank(sessionId) {
    return this.request(`/studio/session/${encodeURIComponent(sessionId)}/assessment-bank`);
  }

  // ---- Academy Operations Console vitals ----
  getOpsVitals() {
    return this.request('/ops/vitals');
  }

  getExecutiveAnalytics({ cohort = 'all', range = '12m', track = 'all' } = {}) {
    const qs = new URLSearchParams({ cohort, range, track }).toString();
    return this.request(`/ops/executive?${qs}`);
  }

  // ---- Cohort management ----
  getCohortOptions() {
    return this.request('/ops/cohorts/options');
  }

  getCohorts() {
    return this.request('/ops/cohorts');
  }

  createCohort(body) {
    return this.request('/ops/cohorts', { method: 'POST', body });
  }

  bulkInviteCohort(cohortId, csv) {
    return this.request(`/ops/cohorts/${encodeURIComponent(cohortId)}/bulk-invite`, {
      method: 'POST',
      body: { csv },
    });
  }

  enrollCohort(cohortId, email) {
    return this.request(`/ops/cohorts/${encodeURIComponent(cohortId)}/enroll`, {
      method: 'POST',
      body: { email },
    });
  }

  // ---- Roles, Permissions & Approvals ----
  getStaff() {
    return this.request('/ops/staff');
  }

  assignStaff(body) {
    return this.request('/ops/staff/assign', { method: 'POST', body });
  }

  getApprovals() {
    return this.request('/ops/approvals');
  }

  resetApprovals() {
    return this.request('/ops/approvals/reset', { method: 'POST' });
  }

  approveBlock(blockCode) {
    return this.request(`/ops/approvals/${encodeURIComponent(blockCode)}/approve`, { method: 'POST' });
  }

  rejectBlock(blockCode, feedback) {
    return this.request(`/ops/approvals/${encodeURIComponent(blockCode)}/reject`, {
      method: 'POST',
      body: { feedback },
    });
  }

  // ---- Certification Criteria Manager ----
  getCriteriaOptions() {
    return this.request('/certification/criteria/options');
  }

  getCriteria() {
    return this.request('/certification/criteria');
  }

  resetCriteria() {
    return this.request('/certification/criteria/reset', { method: 'POST' });
  }

  saveCriteriaDraft(body) {
    return this.request('/certification/criteria/draft', { method: 'POST', body });
  }

  publishCriteria(body) {
    return this.request('/certification/criteria/publish', { method: 'POST', body });
  }

  // ---- Graduate Record Administrator ----
  getGraduateRegistry() {
    return this.request('/certification/registry');
  }

  resetGraduateRegistry() {
    return this.request('/certification/registry/reset', { method: 'POST' });
  }

  getRegistryExpiries() {
    return this.request('/certification/registry/expiries');
  }

  getRegistryAudit() {
    return this.request('/certification/registry/audit');
  }

  revokeCredential(credentialId, reason) {
    return this.request(`/certification/registry/${encodeURIComponent(credentialId)}/revoke`, {
      method: 'POST',
      body: { reason },
    });
  }

  sendRenewalReminder(credentialId) {
    return this.request(`/certification/registry/${encodeURIComponent(credentialId)}/renewal-reminder`, {
      method: 'POST',
    });
  }

  // ---- Coupon & Payment Admin Console ----
  getCouponOptions() {
    return this.request('/commerce/coupons/options');
  }

  getCoupons() {
    return this.request('/commerce/coupons');
  }

  resetCoupons() {
    return this.request('/commerce/coupons/reset', { method: 'POST' });
  }

  generateCoupons(body) {
    return this.request('/commerce/coupons/generate', { method: 'POST', body });
  }

  revokeCoupon(code) {
    return this.request(`/commerce/coupons/${encodeURIComponent(code)}/revoke`, { method: 'POST' });
  }

  getTransactions() {
    return this.request('/commerce/transactions');
  }

  // ---- Job Board Moderation Console ----
  getModerationOptions() {
    return this.request('/moderation/options');
  }

  getModerationConsole() {
    return this.request('/moderation/console');
  }

  resetModeration() {
    return this.request('/moderation/reset', { method: 'POST' });
  }

  approvePosting(postingKey) {
    return this.request(`/moderation/postings/${encodeURIComponent(postingKey)}/approve`, { method: 'POST' });
  }

  rejectPosting(postingKey, body) {
    return this.request(`/moderation/postings/${encodeURIComponent(postingKey)}/reject`, { method: 'POST', body });
  }

  runMatching(postingKey) {
    return this.request(`/moderation/postings/${encodeURIComponent(postingKey)}/match`, { method: 'POST' });
  }

  dispatchReferrals(postingKey) {
    return this.request(`/moderation/postings/${encodeURIComponent(postingKey)}/dispatch`, { method: 'POST' });
  }

  getModerationAudit() {
    return this.request('/moderation/audit');
  }

  // ---- External link health audit (Academy Console → Link & Health) ----
  getLinkHealth() {
    return this.request('/ops/links');
  }

  resetLinkHealth() {
    return this.request('/ops/links/reset', { method: 'POST' });
  }

  scanLinks() {
    return this.request('/ops/links/scan', { method: 'POST' });
  }

  substituteLink(linkKey, url) {
    return this.request(`/ops/links/${encodeURIComponent(linkKey)}/substitute`, { method: 'POST', body: { url } });
  }

  // ---- Employer Operations Workspace ----
  getEmployerWorkspace() {
    return this.request('/employer/workspace');
  }

  // ---- Employer B2B billing ----
  getEmployerBilling() {
    return this.request('/employer/billing');
  }

  employerSubscribe(plan) {
    return this.request('/employer/subscription', { method: 'POST', body: { plan } });
  }

  buyEmployerSponsorship(seats) {
    return this.request('/employer/sponsorships', { method: 'POST', body: { seats } });
  }

  resetEmployerWorkspace() {
    return this.request('/employer/reset', { method: 'POST' });
  }

  createEmployerPosting(body) {
    return this.request('/employer/postings', { method: 'POST', body });
  }

  approveEmployerPosting(postingKey) {
    return this.request(`/employer/postings/${encodeURIComponent(postingKey)}/approve`, { method: 'POST' });
  }

  getEmployerCandidates(postingKey) {
    return this.request(`/employer/postings/${encodeURIComponent(postingKey)}/candidates`);
  }

  updateApplicantStage(applicantId, stage) {
    return this.request(`/employer/applicants/${encodeURIComponent(applicantId)}/stage`, { method: 'PATCH', body: { stage } });
  }

  // ---- Mentor / Course Supervisor Role Landing Hub ----
  getMentorDashboard() {
    return this.request('/mentor/dashboard');
  }

  resetMentorHub() {
    return this.request('/mentor/reset', { method: 'POST' });
  }

  deleteCohortPost(postId) {
    return this.request(`/mentor/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });
  }

  lockCohortThread(postId) {
    return this.request(`/mentor/posts/${encodeURIComponent(postId)}/lock`, { method: 'POST' });
  }

  postAnnouncement(message) {
    return this.request('/mentor/announcements', { method: 'POST', body: { message } });
  }

  addOfficeSlot(slotLabel) {
    return this.request('/mentor/office-slots', { method: 'POST', body: { slotLabel } });
  }

  deleteOfficeSlot(slotId) {
    return this.request(`/mentor/office-slots/${encodeURIComponent(slotId)}`, { method: 'DELETE' });
  }

  // ---- Cohort Communications & Announcements ----
  getCommsOptions() {
    return this.request('/mentor/comms/options');
  }

  getCommsBoard(cohort) {
    return this.request(`/mentor/comms${cohort ? `?cohort=${encodeURIComponent(cohort)}` : ''}`);
  }

  resetComms() {
    return this.request('/mentor/comms/reset', { method: 'POST' });
  }

  publishAnnouncement(body) {
    return this.request('/mentor/comms/announcements', { method: 'POST', body });
  }

  createNotificationSchedule(body) {
    return this.request('/mentor/comms/schedules', { method: 'POST', body });
  }

  cancelNotificationSchedule(scheduleId) {
    return this.request(`/mentor/comms/schedules/${encodeURIComponent(scheduleId)}/cancel`, { method: 'POST' });
  }

  getMentorInbox(search) {
    return this.request(`/mentor/comms/inbox${search ? `?search=${encodeURIComponent(search)}` : ''}`);
  }

  getMentorConversation(conversationId) {
    return this.request(`/mentor/comms/inbox/${encodeURIComponent(conversationId)}`);
  }

  sendMentorReply(conversationId, body) {
    return this.request(`/mentor/comms/inbox/${encodeURIComponent(conversationId)}/reply`, { method: 'POST', body: { body } });
  }

  // ---- Academic Design Agent Console ----
  getAgentConsole() {
    return this.request('/agent/console');
  }

  resetAgentConsole() {
    return this.request('/agent/reset', { method: 'POST' });
  }

  setAgentToggle(key, value) {
    return this.request('/agent/toggle', { method: 'PATCH', body: { key, value } });
  }

  runAgentScan(course) {
    return this.request('/agent/scan', { method: 'POST', body: { course } });
  }

  // ---- Learner Feedback Insights Workspace ----
  getFeedbackWorkspace() {
    return this.request('/insights/workspace');
  }

  resetFeedbackWorkspace() {
    return this.request('/insights/reset', { method: 'POST' });
  }

  generateFeedbackRecommendation({ theme, courseKey } = {}) {
    return this.request('/insights/generate', { method: 'POST', body: { theme, courseKey } });
  }

  triageFeedbackSignal(signalId, state, notes) {
    return this.request(`/insights/signals/${encodeURIComponent(signalId)}/triage`, { method: 'PATCH', body: { state, notes } });
  }

  dispatchFeedbackRecommendation(recId) {
    return this.request(`/insights/recommendations/${encodeURIComponent(recId)}/dispatch`, { method: 'POST' });
  }

  // ---- Learner Management Agent Dashboard ----
  getLmaDashboard() {
    return this.request('/lma/dashboard');
  }

  resetLmaDashboard() {
    return this.request('/lma/reset', { method: 'POST' });
  }

  setLmaStatus(status) {
    return this.request('/lma/status', { method: 'PATCH', body: { status } });
  }

  runLmaScan() {
    return this.request('/lma/scan', { method: 'POST' });
  }

  runLmaEvaluation() {
    return this.request('/lma/evaluate', { method: 'POST' });
  }

  overrideLmaLearner(learnerId, payload) {
    return this.request(`/lma/learners/${encodeURIComponent(learnerId)}/override`, { method: 'POST', body: payload });
  }
}

export default new ApiService();
